
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { Icon } from './icon';

export type Crumb = { label: string; href?: string };

/**
 * The trail above a page title: "Loja › Categoria", "Projetos › Cirurgia plástica › {projeto}".
 * A crumb with `href` is a link; one without is plain text, and the last plain crumb is the
 * current page (`aria-current`). A crumb in the middle may have no page of its own (a project's
 * specialty): it reads as text, never as a dead link. Each link keeps a 24px target (WCAG 2.5.8).
 *
 * `backOnPhone` swaps the trail for a single "‹ {parent}" link under `sm`, to the nearest crumb
 * that has a page: a long title never wraps into three lines above the H1 on a phone. On a
 * touch screen every link keeps a 44px target.
 *
 * Replaces the hand-rolled copies on the store product, catalog and cart pages and the project
 * page (docs/investigations/ui-duplication-audit.md).
 */
export function Breadcrumbs({
  items,
  label,
  backOnPhone = false,
  className,
}: {
  items: Crumb[];
  /** Accessible name of the navigation landmark ("Navegação estrutural"). */
  label: string;
  backOnPhone?: boolean;
  className?: string;
}) {
  // ES2022 lib: no findLastIndex / findLast.
  let lastPlain = -1;
  items.forEach((c, i) => {
    if (!c.href) lastPlain = i;
  });
  const parent = backOnPhone ? [...items].reverse().find((c) => c.href) : undefined;
  return (
    <nav aria-label={label} className={cn('min-w-0 text-[0.8125rem] text-muted-foreground', className)}>
      <ol className={cn('flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1', parent && 'hidden sm:flex')}>
        {items.map((crumb, i) => (
          <li key={`${i}-${crumb.label}`} className="inline-flex min-w-0 items-center gap-1.5">
            {i > 0 && <Icon name="arrow-right-01" size={13} className="shrink-0" />}
            {crumb.href ? (
              <Link href={crumb.href} className="inline-flex min-h-6 items-center hover:text-foreground [@media(pointer:coarse)]:min-h-11">
                {crumb.label}
              </Link>
            ) : (
              <span
                aria-current={i === lastPlain ? 'page' : undefined}
                className={cn(i === lastPlain && 'truncate font-medium text-foreground')}
              >
                {crumb.label}
              </span>
            )}
          </li>
        ))}
      </ol>
      {parent?.href && (
        <Link href={parent.href} className="inline-flex min-h-11 items-center gap-1 font-medium hover:text-foreground sm:hidden">
          <Icon name="arrow-left-01" size={14} />
          {parent.label}
        </Link>
      )}
    </nav>
  );
}
